/**
 * Create / edit a survey — a `FullscreenFormDialog` in the document metaphor
 * (docs/FULLSCREEN_FORM_MODALS.md; precedent `InteractionFormDialog`): the
 * Project link above the card, a thin "Survey" hero, DETAILS (the line above
 * the title, title, description, thank-you message, website button link +
 * text, layout) and the QUESTIONS builder (the shared `QuestionListEditor`,
 * `ui/QuestionListEditor.tsx`: add, remove, and up / down buttons via
 * `move()` — no drag and drop; options, grid rows and grid columns reorder
 * the same way).
 *
 * Mount it conditionally (`{open && <SurveyFormDialog … />}`) — it loads the
 * project list.
 *
 * Validation is the shared `createSurveySchema` (one validation path) via
 * `surveyBuilderResolver`. Once a survey has left `draft` its questions are
 * frozen (DB guard → 409 `survey_frozen`): edit mode then offers only the
 * line above the title, title, description, thank-you message, website button
 * link + text, layout and project, with a note pointing at Duplicate.
 *
 * The "Line above the title" placeholder is the default it falls back to —
 * the shared `surveyIntroLine` rule over the SELECTED project's name (not the
 * `code - name` option label) and the org name, so it follows the picker.
 *
 * Preview (questions band header) shows the CURRENT, unsaved values through
 * `SurveyPreviewDialog`: the builder questions are converted with
 * `toCreateSurveyBody` and checked with the shared `surveyQuestionsSchema`;
 * if they are invalid the form's inline errors show instead. A frozen survey
 * previews its saved questions. The preview logo is the saved survey's public
 * snapshot in edit mode, and the org's CURRENT logo (signed URL, fetched only
 * while the preview is open) before the first save — what Create will copy,
 * shown only for a PNG / JPEG / WebP path (`isCopyableOrgLogoPath`). The
 * preview's website button uses the typed link, else the org's website, and
 * the typed button text, else the default wording — the shared
 * `surveyWebsiteButton` rule, which also fills the "Button text" placeholder
 * ("Visit tickets.example.org" / "Visit {org}").
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState, type FormEvent } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Eye } from 'lucide-react';
import {
  SURVEY_INTRO_LINE_MAX,
  SURVEY_MAX_QUESTIONS,
  SURVEY_THANK_YOU_MAX,
  SURVEY_WEBSITE_LABEL_MAX,
  SURVEY_WEBSITE_URL_MAX,
  surveyQuestionsSchema,
  type SurveyQuestion,
  type SurveyWire,
  type SurveyWriteResponse,
} from '@releve/shared';
import { FullscreenFormDialog } from '@/components/ui/fullscreen-form-dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { DangerZone } from '@/components/ui/danger-zone';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { SearchableSelect, type SearchableSelectOption } from '@/components/SearchableSelect';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useOrganizationBusinessIdentity } from '@/hooks/useOrganizationBusinessIdentity';
import { useProjectIndex } from '@/hooks/useProjectIndex';
import { useSignedUrl } from '@/hooks/useSignedUrl';
import { useCreateSurvey, useDeleteSurvey, useUpdateSurvey } from '@/hooks/useSurveys';
import { useTranslation } from '@/hooks/useTranslation';
import { SurveyLayoutPicker } from './SurveyLayoutPicker';
import { SurveyPreviewDialog, type SurveyPreviewContent } from './SurveyPreviewDialog';
import { isCopyableOrgLogoPath, surveyLogoUrl } from './surveyLogoUrl';
import { surveyIntroLine, surveyWebsiteButtonText } from './surveyLabels';
import { previewSurveyWebsiteButton } from './surveyPreviewContent';
import {
  emptySurveyBuilderValues,
  surveyBuilderResolver,
  surveyToBuilderValues,
  toCreateSurveyBody,
  toUpdateSurveyBody,
  type SurveyBuilderValues,
} from './surveyForm';
import { FieldError, QuestionListEditor } from './ui/QuestionListEditor';

export interface SurveyFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Edit mode when set. */
  survey?: SurveyWire;
  /** After a successful save (the dialog has already closed). */
  onSaved?: (result: SurveyWriteResponse) => void;
  /** After a successful delete (edit mode; the dialog has already closed). */
  onDeleted?: (surveyId: string) => void;
  /** Create mode: pre-select this project (the project's Surveys tab). Ignored when editing. */
  defaultProjectId?: string;
}

const SECTION_HEAD = 'text-xs font-semibold uppercase tracking-wider text-muted-foreground';

export function SurveyFormDialog({ open, onOpenChange, survey, onSaved, onDeleted, defaultProjectId }: SurveyFormDialogProps) {
  const { t } = useTranslation('surveys');
  const isEdit = !!survey;
  const isDraft = !survey || survey.status === 'draft';

  const createSurvey = useCreateSurvey();
  const updateSurvey = useUpdateSurvey();
  const deleteSurvey = useDeleteSurvey();

  const [isDirty, setIsDirty] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const submitRef = useRef<((event: FormEvent) => Promise<void>) | null>(null);

  const defaultValues = useMemo(
    () =>
      survey
        ? surveyToBuilderValues(survey)
        : { ...emptySurveyBuilderValues(), project_id: defaultProjectId ?? '' },
    // Captured once per mount — a refetch never resets what is being typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleValid = async (values: SurveyBuilderValues) => {
    try {
      const result = survey
        ? await updateSurvey.mutateAsync({ surveyId: survey.id, patch: toUpdateSurveyBody(values, isDraft) })
        : await createSurvey.mutateAsync(toCreateSurveyBody(values));
      onOpenChange(false);
      onSaved?.(result);
    } catch {
      // The mutation hook already toasted; keep the form open.
    }
  };

  const handleDelete = async () => {
    if (!survey) return;
    try {
      await deleteSurvey.mutateAsync(survey.id);
      setDeleteOpen(false);
      onOpenChange(false);
      onDeleted?.(survey.id);
    } catch {
      // The mutation hook already toasted.
    }
  };

  const registerSubmit = useCallback((submit: (event: FormEvent) => Promise<void>) => {
    submitRef.current = submit;
  }, []);

  const isSubmitting = createSurvey.isPending || updateSurvey.isPending;

  return (
    <FullscreenFormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? t('dialog.editTitle', { code: survey.code }) : t('dialog.createTitle')}
      description={t('dialog.description')}
      submitLabel={isEdit ? t('dialog.save') : t('dialog.create')}
      submittingLabel={isEdit ? t('dialog.saving') : t('dialog.creating')}
      cancelLabel={t('dialog.cancel')}
      isSubmitting={isSubmitting}
      skipCloseConfirmation={!isDirty}
      onSubmit={(event) => {
        event.preventDefault();
        void submitRef.current?.(event);
      }}
    >
      <SurveyFormBody
        defaultValues={defaultValues}
        onValid={handleValid}
        registerSubmit={registerSubmit}
        onDirtyChange={setIsDirty}
        questionsFrozen={!isDraft}
        savedQuestions={survey?.questions}
        savedLogoPath={survey?.public_logo_path}
        isEdit={isEdit}
        deleting={deleteSurvey.isPending}
        onRequestDelete={() => setDeleteOpen(true)}
      />

      {survey && deleteOpen && (
        <ConfirmDialog
          open
          onOpenChange={setDeleteOpen}
          onConfirm={handleDelete}
          title={t('delete.title')}
          description={t('delete.description', { code: survey.code, count: survey.response_count })}
          confirmText={t('delete.confirm')}
          cancelText={t('delete.cancel')}
          variant="destructive"
        />
      )}
    </FullscreenFormDialog>
  );
}

/* ---------------------------------- body --------------------------------- */

interface SurveyFormBodyProps {
  defaultValues: SurveyBuilderValues;
  onValid: (values: SurveyBuilderValues) => Promise<void>;
  registerSubmit: (submit: (event: FormEvent) => Promise<void>) => void;
  onDirtyChange: (dirty: boolean) => void;
  questionsFrozen: boolean;
  /** A frozen survey's stored questions — what Preview shows when the builder cannot edit them. */
  savedQuestions?: readonly SurveyQuestion[];
  /** Edit mode: the saved survey's logo snapshot (what Preview shows; NULL → no logo). */
  savedLogoPath?: string | null;
  /** Edit mode: the form ends with a DangerZone (the InteractionFormDialog pattern). */
  isEdit?: boolean;
  deleting?: boolean;
  onRequestDelete?: () => void;
}

export function SurveyFormBody({
  defaultValues,
  onValid,
  registerSubmit,
  onDirtyChange,
  questionsFrozen,
  savedQuestions,
  savedLogoPath,
  isEdit = false,
  deleting = false,
  onRequestDelete,
}: SurveyFormBodyProps) {
  const { t } = useTranslation('surveys');
  const displayModeLabelId = useId();
  const { selectedOrganization } = useOrganization();
  const resolver = useMemo(() => surveyBuilderResolver(t), [t]);

  const form = useForm<SurveyBuilderValues>({ resolver, defaultValues });

  const onValidRef = useRef(onValid);
  onValidRef.current = onValid;
  useEffect(() => {
    registerSubmit(form.handleSubmit((values) => onValidRef.current(values)));
  }, [form, registerSubmit]);

  const { isDirty } = form.formState;
  useEffect(() => {
    onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  const { data: projects = [] } = useProjectIndex(selectedOrganization?.id);
  const projectOptions = useMemo<SearchableSelectOption[]>(
    () => projects.map((p) => ({ value: p.id, label: `${p.code} - ${p.name}` })),
    [projects],
  );

  // The "Line above the title" placeholder: the default for the SELECTED project's name.
  const selectedProjectId = useWatch({ control: form.control, name: 'project_id' });
  const selectedProjectName = projects.find((p) => p.id === selectedProjectId)?.name ?? null;
  const orgName = selectedOrganization?.name ?? '';
  const defaultIntroLine = surveyIntroLine({ introLine: null, projectName: selectedProjectName, orgName }, t).text;

  const questionsError = (form.formState.errors.questions as { message?: string } | undefined)?.message;

  // The org's website: the placeholder of the override field (what an empty
  // override falls back to).
  const { data: identity } = useOrganizationBusinessIdentity(selectedOrganization?.id);
  const orgWebsite = identity?.website_url?.trim() || '';
  // The "Button text" placeholder: what an empty text will say for the typed
  // link (the same shared rule as the public page).
  const typedWebsiteUrl = useWatch({ control: form.control, name: 'website_url' });
  const defaultWebsiteButton = previewSurveyWebsiteButton({
    override: typedWebsiteUrl,
    label: null,
    orgWebsite,
    orgName,
  });

  const [preview, setPreview] = useState<SurveyPreviewContent | null>(null);
  // Before the first save the preview shows the org's CURRENT logo (what Create
  // copies) — only when its format is one the copy admits (PNG / JPEG / WebP);
  // signed only while the preview is open.
  const currentOrgLogo = isEdit ? null : selectedOrganization?.logo_medium || selectedOrganization?.logo_large || null;
  const orgLogoPath = isCopyableOrgLogoPath(currentOrgLogo) ? currentOrgLogo : null;
  const { data: orgLogoUrl } = useSignedUrl(preview ? orgLogoPath : null);
  const previewLogoUrl = isEdit ? surveyLogoUrl(savedLogoPath) : (orgLogoUrl ?? null);

  const openPreview = () => {
    const values = form.getValues();
    const { title, description } = values;
    const thankYouMessage = values.thank_you_message.trim() || null;
    const websiteUrl = values.website_url.trim() || null;
    const websiteLabel = values.website_label.trim() || null;
    const page = {
      title,
      description,
      thankYouMessage,
      websiteUrl,
      websiteLabel,
      introLine: values.intro_line.trim() || null,
      projectName: selectedProjectName,
      displayMode: values.display_mode,
    };
    if (questionsFrozen) {
      setPreview({ ...page, questions: savedQuestions ?? [] });
      return;
    }
    const parsed = surveyQuestionsSchema.safeParse(toCreateSurveyBody(values).questions);
    if (!parsed.success) {
      // Same shared rules as Save: show the inline errors instead of a broken preview.
      void form.trigger('questions');
      return;
    }
    setPreview({ ...page, questions: parsed.data });
  };

  return (
    <Form {...form}>
      <div className="space-y-8">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <FormField
            control={form.control}
            name="project_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('dialog.fields.project')}</FormLabel>
                <FormControl>
                  <SearchableSelect
                    options={projectOptions}
                    value={field.value}
                    onValueChange={field.onChange}
                    placeholder={t('dialog.placeholders.project')}
                    searchPlaceholder={t('dialog.placeholders.projectSearch')}
                    emptyText={t('dialog.empty.project')}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <Card>
          <CardContent className="space-y-8 pt-6">
            <h2 className="text-right text-[40px] font-extralight uppercase leading-tight">{t('dialog.hero')}</h2>

            <section className="space-y-3">
              <h3 className={SECTION_HEAD}>{t('dialog.sections.details')}</h3>
              {/* Stays editable after opening (not frozen). Empty → the placeholder's default. */}
              <FormField
                control={form.control}
                name="intro_line"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel>{t('dialog.fields.introLine')}</FormLabel>
                      <FieldError message={fieldState.error?.message} />
                    </div>
                    <FormControl>
                      <Input
                        {...field}
                        maxLength={SURVEY_INTRO_LINE_MAX}
                        placeholder={defaultIntroLine}
                        className={fieldState.error ? 'border-destructive' : undefined}
                      />
                    </FormControl>
                    <FormDescription className="text-xs">{t('dialog.help.introLine')}</FormDescription>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="title"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel>{t('dialog.fields.title')}</FormLabel>
                      <FieldError message={fieldState.error?.message} />
                    </div>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={t('dialog.placeholders.title')}
                        className={fieldState.error ? 'border-destructive' : undefined}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel>{t('dialog.fields.description')}</FormLabel>
                      <FieldError message={fieldState.error?.message} />
                    </div>
                    <FormControl>
                      <Textarea
                        {...field}
                        rows={3}
                        placeholder={t('dialog.placeholders.description')}
                        className={fieldState.error ? 'border-destructive' : undefined}
                      />
                    </FormControl>
                    <FormDescription className="text-xs">{t('dialog.help.description')}</FormDescription>
                  </FormItem>
                )}
              />
              {/* Stays editable after opening (the DB freeze guard covers questions only). */}
              <FormField
                control={form.control}
                name="thank_you_message"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel>{t('dialog.fields.thankYouMessage')}</FormLabel>
                      <FieldError message={fieldState.error?.message} />
                    </div>
                    <FormControl>
                      <Textarea
                        {...field}
                        rows={2}
                        maxLength={SURVEY_THANK_YOU_MAX}
                        placeholder={t('dialog.placeholders.thankYouMessage')}
                        className={fieldState.error ? 'border-destructive' : undefined}
                      />
                    </FormControl>
                    <FormDescription className="text-xs">{t('dialog.help.thankYouMessage')}</FormDescription>
                  </FormItem>
                )}
              />
              {/* Button text and link side by side (stacked on small screens). */}
              <div className="grid gap-5 sm:grid-cols-2 sm:items-start">
                <FormField
                  control={form.control}
                  name="website_label"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <div className="flex items-center gap-2">
                        <FormLabel>{t('dialog.fields.websiteLabel')}</FormLabel>
                        <FieldError message={fieldState.error?.message} />
                      </div>
                      <FormControl>
                        <Input
                          {...field}
                          maxLength={SURVEY_WEBSITE_LABEL_MAX}
                          placeholder={
                            defaultWebsiteButton
                              ? surveyWebsiteButtonText(defaultWebsiteButton, t)
                              : t('dialog.placeholders.websiteLabel')
                          }
                          className={fieldState.error ? 'border-destructive' : undefined}
                        />
                      </FormControl>
                      <FormDescription className="text-xs">{t('dialog.help.websiteLabel')}</FormDescription>
                    </FormItem>
                  )}
                />
                {/* Also editable after opening; checked by the shared normaliser (createSurveySchema). */}
                <FormField
                  control={form.control}
                  name="website_url"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <div className="flex items-center gap-2">
                        <FormLabel>{t('dialog.fields.websiteUrl')}</FormLabel>
                        <FieldError message={fieldState.error?.message} />
                      </div>
                      <FormControl>
                        <Input
                          {...field}
                          // Not type="url": native validation would refuse a scheme-less `acme.nl`, which the normaliser accepts.
                          inputMode="url"
                          autoComplete="url"
                          maxLength={SURVEY_WEBSITE_URL_MAX}
                          placeholder={orgWebsite || t('dialog.placeholders.websiteUrl')}
                          className={fieldState.error ? 'border-destructive' : undefined}
                        />
                      </FormControl>
                      <FormDescription className="text-xs">
                        {orgWebsite ? t('dialog.help.websiteUrl') : t('dialog.help.websiteUrlNoDefault')}
                      </FormDescription>
                    </FormItem>
                  )}
                />
              </div>
              {/* Also editable after opening: the answers have the same shape in either layout. */}
              <FormField
                control={form.control}
                name="display_mode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel id={displayModeLabelId}>{t('dialog.fields.displayMode')}</FormLabel>
                    <FormControl>
                      <SurveyLayoutPicker value={field.value} onChange={field.onChange} labelledBy={displayModeLabelId} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </section>

            {/* `-mb-6` bleeds the band through CardContent's bottom padding to the card edge —
                sound only because this is the LAST section; `rounded-b-lg` matches the Card's radius
                (rounding here, not `overflow-hidden` on the Card, keeps portaled Selects unclipped). */}
            <section className="-mx-6 -mb-6 space-y-5 rounded-b-lg bg-muted px-6 py-6">
              <div className="flex items-center gap-2">
                <h3 className={SECTION_HEAD}>{t('dialog.sections.questions')}</h3>
                {!questionsFrozen && <FieldError message={questionsError} />}
                <Button type="button" variant="outline" size="sm" className="ml-auto" onClick={openPreview}>
                  <Eye className="mr-1 h-4 w-4" />
                  {t('preview.button')}
                </Button>
              </div>
              {questionsFrozen ? (
                <Alert>
                  <AlertDescription>{t('dialog.frozenNote')}</AlertDescription>
                </Alert>
              ) : (
                <QuestionListEditor control={form.control} max={SURVEY_MAX_QUESTIONS} />
              )}
            </section>
          </CardContent>
        </Card>

        {isEdit && onRequestDelete && (
          <DangerZone
            title={t('dialog.dangerZone.title')}
            actionTitle={t('dialog.dangerZone.deleteTitle')}
            description={t('dialog.dangerZone.deleteDescription')}
            buttonLabel={t('dialog.dangerZone.deleteButton')}
            onAction={onRequestDelete}
            disabled={deleting}
          />
        )}
      </div>

      {preview && (
        <SurveyPreviewDialog
          open
          onOpenChange={(open) => !open && setPreview(null)}
          survey={{ ...preview, logoUrl: previewLogoUrl }}
        />
      )}
    </Form>
  );
}
